import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserTransactions, getUserWallet } from '../services/walletServiceEnhanced';
import Loader from '../components/Loader';

import {
    isCredit,
    isOnChain,
    isReversed,
    txLabel,
    txIcon,
    timeAgo,
    formatUsd,
    txRowAmountColor,
    txAmountPrefix,
} from '../utils/transactionDisplay';

const FILTERS = [
    { key: 'all',      label: 'All' },
    { key: 'credits',  label: 'Credits' },
    { key: 'debits',   label: 'Debits' },
    { key: 'onchain',  label: 'On-Chain' },
    { key: 'offchain', label: 'Off-Chain' },
];

function AcountStatement() {
    const { user } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [wallet, setWallet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        if (user?.id) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const loadData = async () => {
        const [txResult, walletResult] = await Promise.all([
            getUserTransactions(user.id),
            getUserWallet(user.id),
        ]);
        if (!txResult.error && txResult.data) setTransactions(txResult.data);
        if (!walletResult.error && walletResult.data) setWallet(walletResult.data);
        setLoading(false);
    };

    const filtered = transactions.filter(tx => {
        if (filter === 'credits')  return isCredit(tx.transaction_type);
        if (filter === 'debits')   return !isCredit(tx.transaction_type);
        if (filter === 'onchain')  return isOnChain(tx);
        if (filter === 'offchain') return !isOnChain(tx);
        return true;
    });

    // Number() guards against amount_usd arriving as a string: DECIMAL columns can be
    // serialised either way, and `0 + "10.00" + "5.00"` silently produces NaN.
    const settled = transactions.filter(tx => !isReversed(tx));
    const totalCredits = settled.filter(tx => isCredit(tx.transaction_type)).reduce((s, tx) => s + (Number(tx.amount_usd) || 0), 0);
    const totalDebits  = settled.filter(tx => !isCredit(tx.transaction_type)).reduce((s, tx) => s + (Number(tx.amount_usd) || 0), 0);

    if (loading) {
        return (
            <main className="main-sectionWrapper">
                <div id="main-section" className="primary-scroll">
                    <div style={s.headerBar}>
                        <Link to="/wallet" style={s.backBtn} aria-label="Back">
                            <i className='bx bx-chevron-left' style={s.backIcon}></i>
                        </Link>
                        <span style={s.headerTitle}>Account Statement</span>
                        <div style={{ width: 38 }} />
                    </div>
                    <Loader />
                </div>
            </main>
        );
    }

    return (
        <main className="main-sectionWrapper">
            <div id="main-section" className="primary-scroll">

                {/* Header */}
                <div style={s.headerBar}>
                    <Link to="/wallet" style={s.backBtn} aria-label="Back">
                        <i className='bx bx-chevron-left' style={s.backIcon}></i>
                    </Link>
                    <span style={s.headerTitle}>Account Statement</span>
                    <div style={{ width: 38 }} />
                </div>

                <div style={s.container}>

                    {/* Summary chips */}
                    <div style={s.summaryRow}>
                        <div style={s.summaryChip}>
                            <span style={s.summaryLabel}>Balance</span>
                            <span style={s.summaryValue}>${formatUsd(wallet?.real_balance_usd)}</span>
                        </div>
                        <div style={{ ...s.summaryChip, ...s.summaryChipGreen }}>
                            <span style={s.summaryLabel}>Credits</span>
                            <span style={{ ...s.summaryValue, color: '#10b981' }}>+${formatUsd(totalCredits)}</span>
                        </div>
                        <div style={{ ...s.summaryChip, ...s.summaryChipRed }}>
                            <span style={s.summaryLabel}>Debits</span>
                            <span style={{ ...s.summaryValue, color: '#ef4444' }}>-${formatUsd(totalDebits)}</span>
                        </div>
                    </div>

                    {/* Filter pills */}
                    <div style={s.filterPills}>
                        {FILTERS.map(f => (
                            <button
                                key={f.key}
                                style={{ ...s.filterPill, ...(filter === f.key ? s.filterPillActive : {}) }}
                                onClick={() => setFilter(f.key)}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {/* Transactions list */}
                    <div style={s.txCard}>
                        {filtered.length === 0 ? (
                            <div style={s.emptyState}>
                                <div style={s.emptyIcon}>
                                    <i className="bx bx-receipt" style={{ fontSize: 28, color: 'rgba(255,255,255,0.3)' }} />
                                </div>
                                <p style={s.emptyText}>No transactions found</p>
                                <p style={s.emptySubtext}>Try a different filter</p>
                            </div>
                        ) : (
                            filtered.map((tx, idx) => {
                                const credit = isCredit(tx.transaction_type);
                                const onchain = isOnChain(tx);
                                return (
                                    <div
                                        key={tx.id}
                                        style={{ ...s.txRow, ...(idx < filtered.length - 1 ? s.txRowBorder : {}) }}
                                    >
                                        <div style={{
                                            ...s.txIconWrap,
                                            background: credit ? 'rgba(5,150,105,0.15)' : 'rgba(239,68,68,0.12)',
                                        }}>
                                            <i
                                                className={`bx ${txIcon(tx.transaction_type)}`}
                                                style={{ color: txRowAmountColor(tx, credit), fontSize: 18 }}
                                            />
                                        </div>
                                        <div style={s.txInfo}>
                                            <div style={s.txTitleRow}>
                                                <span style={s.txTitleText}>{txLabel(tx)}</span>
                                                <span style={{
                                                    ...s.txBadge,
                                                    ...(onchain ? s.txBadgeChain : s.txBadgeOff),
                                                }}>
                                                    {onchain ? 'On-Chain' : 'Off-Chain'}
                                                </span>
                                                {isReversed(tx) && (
                                                    <span style={{ ...s.txBadge, ...s.txBadgeReversed }}>Reversed</span>
                                                )}
                                            </div>
                                            <div style={s.txDate}>{timeAgo(tx.created_at)}</div>
                                        </div>
                                        <div style={{
                                            ...s.txAmount,
                                            color: txRowAmountColor(tx, credit),
                                            textDecoration: isReversed(tx) ? 'line-through' : 'none',
                                        }}>
                                            {txAmountPrefix(tx, credit)}${formatUsd(tx.amount_usd)}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <p style={s.txCount}>{transactions.length} transaction{transactions.length !== 1 ? 's' : ''} total</p>

                </div>
            </div>
        </main>
    );
}

const s = {
    /* Header */
    headerBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 14px',
        background: 'rgba(14,7,88,0.88)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    backBtn: {
        width: 38,
        height: 38,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        flexShrink: 0,
    },
    backIcon: {
        color: '#fff',
        fontSize: 22,
    },
    headerTitle: {
        color: '#fff',
        fontSize: 16,
        fontWeight: 700,
        letterSpacing: '-0.2px',
        flex: 1,
        textAlign: 'center',
    },

    /* Container */
    container: {
        padding: '16px 16px 96px',
        maxWidth: 600,
        margin: '0 auto',
    },

    /* Summary row */
    summaryRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 10,
        marginBottom: 16,
    },
    summaryChip: {
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 14,
        padding: '12px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        textAlign: 'center',
    },
    summaryChipGreen: {
        background: 'rgba(5,150,105,0.08)',
        border: '1px solid rgba(5,150,105,0.2)',
    },
    summaryChipRed: {
        background: 'rgba(220,38,38,0.08)',
        border: '1px solid rgba(220,38,38,0.18)',
    },
    summaryLabel: {
        color: 'rgba(255,255,255,0.45)',
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        fontWeight: 600,
    },
    summaryValue: {
        color: '#fff',
        fontSize: 14,
        fontWeight: 700,
    },

    /* Filter pills */
    filterPills: {
        display: 'flex',
        gap: 8,
        marginBottom: 16,
        overflowX: 'auto',
        paddingBottom: 4,
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
    },
    filterPill: {
        flexShrink: 0,
        padding: '7px 16px',
        borderRadius: 20,
        border: '1px solid rgba(255,255,255,0.1)',
        background: 'transparent',
        color: 'rgba(255,255,255,0.5)',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
    },
    filterPillActive: {
        background: 'rgba(25,191,177,0.15)',
        border: '1px solid rgba(25,191,177,0.4)',
        color: '#19bfb1',
    },

    /* Transaction card */
    txCard: {
        background: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 20,
        overflow: 'hidden',
        marginBottom: 12,
    },
    txRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
    },
    txRowBorder: {
        borderBottom: '1px solid rgba(255,255,255,0.04)',
    },
    txIconWrap: {
        width: 40,
        height: 40,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    txInfo: {
        flex: 1,
        minWidth: 0,
    },
    txTitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginBottom: 3,
    },
    txTitleText: {
        color: '#fff',
        fontSize: 14,
        fontWeight: 500,
        flex: 1,
        minWidth: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    txBadge: {
        flexShrink: 0,
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: 20,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
    },
    txBadgeChain: {
        background: 'rgba(25,191,177,0.12)',
        color: '#19bfb1',
        border: '1px solid rgba(25,191,177,0.25)',
    },
    txBadgeOff: {
        background: 'rgba(59,130,246,0.12)',
        color: '#3b82f6',
        border: '1px solid rgba(59,130,246,0.25)',
    },
    txBadgeReversed: {
        background: 'rgba(148,163,184,0.15)',
        color: 'rgba(255,255,255,0.6)',
        border: '1px solid rgba(148,163,184,0.3)',
        marginLeft: 6,
    },
    txDate: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 12,
    },
    txAmount: {
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
    },

    /* Empty state */
    emptyState: {
        textAlign: 'center',
        padding: '40px 20px',
    },
    emptyIcon: {
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.04)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 12px',
    },
    emptyText: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 14,
        fontWeight: 500,
        margin: '0 0 4px',
    },
    emptySubtext: {
        color: 'rgba(255,255,255,0.3)',
        fontSize: 12,
        margin: 0,
    },

    /* Footer count */
    txCount: {
        color: 'rgba(255,255,255,0.3)',
        fontSize: 12,
        textAlign: 'center',
        margin: 0,
    },
};

export default AcountStatement;
